import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

interface AboutInfo {
  version: string;
  created_by: string;
  built_with: string;
  company_name: string;
  department: string;
  director: string;
  co_presidents: string[];
  founded_year: number;
  headquarters: string;
  facility_size: string;
  updated_at: string;
}

const sb = supabase as unknown as {
  from: (t: string) => {
    select: (cols: string) => {
      eq: (col: string, val: number) => {
        maybeSingle: () => Promise<{ data: AboutInfo | null; error: { message: string } | null }>;
      };
    };
    update: (patch: object) => {
      eq: (col: string, val: number) => Promise<{ error: { message: string } | null }>;
    };
  };
};

interface Props {
  onClose: () => void;
}

export function AboutModal({ onClose }: Props) {
  const { isAdmin, isCoAdmin } = useAuth();
  const canEdit = isAdmin || isCoAdmin;

  const [info, setInfo] = useState<AboutInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<AboutInfo | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error: err } = await sb
        .from('about_info')
        .select('version,created_by,built_with,company_name,department,director,co_presidents,founded_year,headquarters,facility_size,updated_at')
        .eq('id', 1)
        .maybeSingle();
      if (err) throw new Error(err.message);
      setInfo(data);
      setDraft(data);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function save() {
    if (!draft) return;
    setBusy(true);
    setError(null);
    try {
      // NOTE: created_by is intentionally NOT included here — it is a locked
      // field and must never be overwritten from the UI.
      const { error: err } = await sb.from('about_info').update({
        version: draft.version,
        built_with: draft.built_with,
        company_name: draft.company_name,
        department: draft.department,
        director: draft.director,
        co_presidents: draft.co_presidents,
        founded_year: draft.founded_year,
        headquarters: draft.headquarters,
        facility_size: draft.facility_size,
      }).eq('id', 1);
      if (err) throw new Error(err.message);
      await load();
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  }

  function patch<K extends keyof AboutInfo>(k: K, v: AboutInfo[K]) {
    setDraft((d) => (d ? { ...d, [k]: v } : d));
  }

  return (
    <>
      <div className="lab-drawer-scrim" onClick={onClose} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 'min(560px, 92vw)', maxHeight: '90vh', overflowY: 'auto', background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 14, boxShadow: 'var(--shadow-lg)', zIndex: 200 }}>
        <div style={{ background: 'linear-gradient(110deg, #0f0f12 0%, #1e1e22 60%, #1e88c9 160%)', color: '#fff', padding: '28px 28px 24px', position: 'relative', borderTopLeftRadius: 14, borderTopRightRadius: 14 }}>
          <button type="button" onClick={onClose} style={{ position: 'absolute', top: 12, right: 12, background: 'rgba(255,255,255,.12)', border: 'none', color: '#fff', width: 30, height: 30, borderRadius: '50%', cursor: 'pointer', fontSize: 14, fontFamily: 'inherit' }}>✕</button>
          <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 2.5, textTransform: 'uppercase', opacity: 0.7, marginBottom: 10 }}>About</div>
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-.02em', lineHeight: 1.2, marginBottom: 6 }}>Liteline Engineering Portal</div>
          {info && <div style={{ fontSize: 12, opacity: 0.8 }}>Version <b>{info.version}</b></div>}
        </div>

        <div style={{ padding: '20px 28px 22px' }}>
          {loading ? (
            <div style={{ color: 'var(--muted)' }}>Loading…</div>
          ) : !info || !draft ? (
            <div style={{ color: 'var(--danger)' }}>Could not load about info. {error && <>· {error}</>}</div>
          ) : editing ? (
            <EditForm draft={draft} patch={patch} busy={busy} />
          ) : (
            <ViewBody info={info} />
          )}

          {error && !loading && (
            <div className="login-error" style={{ marginTop: 12 }}>{error}</div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)', alignItems: 'center' }}>
            {info && (
              <div style={{ fontSize: 10.5, color: 'var(--muted)', flex: 1 }}>
                Last updated {new Date(info.updated_at).toLocaleDateString()}
              </div>
            )}
            {canEdit && !editing && (
              <button type="button" className="btn btn-sm btn-out" onClick={() => setEditing(true)}>✎ Edit</button>
            )}
            {editing && (
              <>
                <button type="button" className="btn btn-sm btn-out" onClick={() => { setDraft(info); setEditing(false); }} disabled={busy}>Cancel</button>
                <button type="button" className="btn btn-sm btn-blue" onClick={() => { void save(); }} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
              </>
            )}
            {!editing && (
              <button type="button" className="btn btn-sm btn-blue" onClick={onClose}>Close</button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function ViewBody({ info }: { info: AboutInfo }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Row label="Created by" value={info.created_by} />
      <Row label="Built with" value={info.built_with} />
      <Divider />
      <Row label="Company" value={info.company_name} />
      <Row label="Founded" value={`${info.founded_year} · ${info.headquarters}`} />
      <Row label="Facility" value={info.facility_size} />
      <Divider />
      <Row label="Department" value={info.department} />
      <Row label="Director" value={info.director} />
      <Row label="Co-Presidents" value={info.co_presidents.join('\n')} multiline />
    </div>
  );
}

function Row({ label, value, multiline }: { label: string; value: string; multiline?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.6px', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 13.5, color: 'var(--text)', fontWeight: 600, whiteSpace: multiline ? 'pre-line' : 'normal', lineHeight: 1.5 }}>{value}</div>
    </div>
  );
}

function Divider() {
  return <div style={{ height: 1, background: 'var(--border)' }} />;
}

function EditForm({
  draft,
  patch,
  busy,
}: {
  draft: AboutInfo;
  patch: <K extends keyof AboutInfo>(k: K, v: AboutInfo[K]) => void;
  busy: boolean;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <Edit label="Version" value={draft.version} onChange={(v) => patch('version', v)} disabled={busy} />
      {/* Created by is locked — shown for reference but cannot be edited. */}
      <Edit label="Created by (locked)" value={draft.created_by} onChange={() => undefined} disabled locked />
      <Edit label="Built with" value={draft.built_with} onChange={(v) => patch('built_with', v)} disabled={busy} />
      <Edit label="Company name" value={draft.company_name} onChange={(v) => patch('company_name', v)} disabled={busy} />
      <Edit label="Department" value={draft.department} onChange={(v) => patch('department', v)} disabled={busy} />
      <Edit label="Director" value={draft.director} onChange={(v) => patch('director', v)} disabled={busy} />
      <Edit label="Co-Presidents (one per line)" value={draft.co_presidents.join('\n')} onChange={(v) => patch('co_presidents', v.split('\n').map((s) => s.trim()).filter(Boolean))} disabled={busy} multiline />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Edit label="Founded year" value={String(draft.founded_year)} onChange={(v) => patch('founded_year', parseInt(v, 10) || draft.founded_year)} disabled={busy} />
        <Edit label="Facility size" value={draft.facility_size} onChange={(v) => patch('facility_size', v)} disabled={busy} />
      </div>
      <Edit label="Headquarters" value={draft.headquarters} onChange={(v) => patch('headquarters', v)} disabled={busy} />
    </div>
  );
}

function Edit({
  label,
  value,
  onChange,
  disabled,
  multiline,
  locked,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  multiline?: boolean;
  locked?: boolean;
}) {
  const baseStyle: React.CSSProperties = {
    padding: '7px 10px',
    border: '1.5px solid var(--border)',
    borderRadius: 6,
    fontSize: 13,
    background: locked ? 'var(--gray-1)' : 'var(--input)',
    color: locked ? 'var(--muted)' : 'var(--text)',
    fontFamily: 'inherit',
    cursor: locked ? 'not-allowed' : undefined,
  };
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.6px', textTransform: 'uppercase', color: 'var(--muted)' }}>{label}</span>
      {multiline ? (
        <textarea rows={3} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} style={{ ...baseStyle, resize: 'vertical' }} />
      ) : (
        <input value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} readOnly={locked} style={baseStyle} />
      )}
    </label>
  );
}
